import { useRef, useState } from "react";
import {
  ArtButton,
  ArtActionMenu,
  ArtDialog,
  ArtTable,
  ArtTableHandle,
  FaIcon,
  confirmDialog,
  HeadCell,
  SSRTableBuilder,
  useIdpService,
} from "@art/shared-ui";
import { faEdit, faPlus, faShieldHalved, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AttributePermissionGate, useAttributePermission } from "../../permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { usePageWrapper } from "../../../hooks/page-wrapper.hook";
import RolePermissionAddPage from "./role-form-dialog";
import { tableParamNav } from "../../../utils/table-utils";

interface RoleRow {
  id: number;
  name: string;
  description: string;
  is_default?: boolean;
  tenant_id?: number | null;
  actions?: string;
}

const RolesPermissions = () => {
  const idpService = useIdpService();
  const navigate = useNavigate();
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const isMasterTenant = useSelector((state: RootState) => Boolean(state.auth.user?.tenant?.is_master));
  const canEditRoles = useAttributePermission("PLATFORM_SETTINGS", "ROLE_MANAGEMENT", "EditRoles");
  const canDeleteRoles = useAttributePermission("PLATFORM_SETTINGS", "ROLE_MANAGEMENT", "DeleteRoles");
  const canViewRoleDetails = useAttributePermission("PLATFORM_SETTINGS", "ROLE_MANAGEMENT", "ViewRoleDetails");
  const canManageRolePermissions = useAttributePermission("PLATFORM_SETTINGS", "ROLE_MANAGEMENT", "ManageRolePermissions");

  const [queryParams, setQueryParams]: any = useSearchParams();
  const tableRef = useRef<ArtTableHandle>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const [tableSearchValue, setTableSearchValue] = useState(queryParams.get("search") || "");
  const [selectedData, setSelectedData] = useState<RoleRow | null>(null);

  const goToRole = (row: RoleRow) => navigate(`/platform-settings/roles-permissions/${row.id}`);
  const canModifyRole = (row: RoleRow) => !row.is_default && (isMasterTenant || row.tenant_id != null);

  const headCells: HeadCell<RoleRow>[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
      getColData: (row) => <div onClick={() => goToRole(row)} >
        {row.name}
      </div>
      ,
    },
    {
      id: "description",
      numeric: false,
      disablePadding: false,
      label: "Description",
      getColData: (row) => <div onClick={() => goToRole(row)} >
        {row.description}
      </div>
      ,
    },
    {
      id: "actions",
      numeric: false,
      disablePadding: false,
      label: "Actions",
      getColData: (row) => {
        const editable = canModifyRole(row);
        const options = [
          ...(canManageRolePermissions ? [{
            label: editable ? "Manage access" : "View access",
            icon: <FaIcon icon={faShieldHalved} />,
            actions: [() => navigate(`/platform-settings/roles-permissions/${row.id}/abac`)],
          }] : canViewRoleDetails ? [{
            label: "View role",
            icon: <FaIcon icon={faShieldHalved} />,
            actions: [() => goToRole(row)],
          }] : []),
          ...(editable && canEditRoles ? [{
            label: "Edit",
            icon: <FaIcon icon={faEdit} />,
            actions: [() => openAddRoleDialog(row)],
          }] : []),
          ...(editable && canDeleteRoles ? [{
            label: "Delete",
            icon: <FaIcon icon={faTrash} />,
            actions: [() => handleDelete(row)],
          }] : []),
        ];
        return options.length ? <ArtActionMenu dataQa={`role-actions-${row.id}`} options={options} /> : null;
      },
    },
  ];

  const tableBuilder = SSRTableBuilder<RoleRow>({
    endpoint: `api/tenants/${tenantName}/roles`,
    method: "get",
    apiService: idpService,
    onFetch: (params) => {
      const currentPage = params["page"] || "1";
      const searchText = params["search_text"] || "";

      const newParams: any = { page: currentPage };
      if (searchText) newParams.search = searchText;
      setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage));

      return {
        page: currentPage,
        limit: 10,
        search_text: searchText,
      };
    },
    onResponse: (data) => {
      const roles: RoleRow[] = data?.data?.role || [];
      return {
        data: roles,
        total: data?.data?.total ?? roles.length,
      };
    },
  });

  const openAddRoleDialog = (data?: RoleRow) => {
    setSelectedData(data || null);
    setAddDialogOpen(true);
  };

  const closeAddRoleDialog = () => {
    setSelectedData(null);
    setAddDialogOpen(false);
  };

  const onSave = () => {
    closeAddRoleDialog();
    setTableSearchValue("");
    setTableKey((prev) => prev + 1);
  };

  const handleDelete = (row: RoleRow) => {
    confirmDialog("confirm", "Do you want to delete?").then(() => {
      idpService.deleteData(`api/tenants/${tenantName}/roles/${row.id}`, {
        onSuccess: () => {
          onSave();
        },
        onError: (err: any) => {
          console.error(err);
        },
      })();
    });
  };

  return (
    <>
      <ArtDialog
        title={selectedData ? "Edit Role" : "Add Role"}
        open={addDialogOpen}
        onClose={closeAddRoleDialog}
        type="form"
        actionBuilder={(props) => (
          <Box sx={{ display: "flex", gap: "15px" }}>
            <ArtButton
              buttonType="button"
              dataQa="btn-save"
              label={selectedData ? "Update" : "Add"}
              onClick={() => props.submit?.()}
            />
            <ArtButton
              buttonType="button"
              dataQa="btn-reset"
              label="Reset"
              variant="outlined"
              color="error"
              onClick={() => props.reset?.()}
            />
          </Box>
        )}
      >
        <RolePermissionAddPage data={selectedData} onSave={onSave} tenantName={tenantName} />
      </ArtDialog>

      <Box>
        <ArtTable
          key={tableKey}
          ref={tableRef}
          headCells={headCells}
          builder={tableBuilder}
          search={{
            enabled: true,
            value: tableSearchValue,
          }}
          pagination={{
            initialPage: parseInt(queryParams.get("page") || "1") - 1,
          }}
          toolbarActions={[
            <AttributePermissionGate application="PLATFORM_SETTINGS" feature="ROLE_MANAGEMENT" attribute="CreateRoles">
              <ArtButton
                variant="contained"
                label="Add"
                dataQa="btn-add"
                icon={faPlus}
                onClick={() => openAddRoleDialog()}
              />
            </AttributePermissionGate>,
          ]}
        />
      </Box>
    </>
  );
};

export default usePageWrapper(RolesPermissions, {
  page_title: "Roles & Permissions",
});
